import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import type { ApiError, ApiErrorItem } from "../types/api";

const httpClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
});

function getCookie(name: string): string {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length !== 2) {
    return "";
  }

  return parts.pop()?.split(";").shift() || "";
}

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = String(config.method || "get").toUpperCase();
  const csrfToken = getCookie("csrf_token");

  if (csrfToken && ["POST", "PUT", "DELETE"].includes(method)) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = csrfToken;
  }

  return config;
});

httpClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<Record<string, unknown>>) => {
    const data = error.response?.data ?? null;
    const dataObj: Record<string, unknown> = data && typeof data === "object" ? data : {};
    const firstError = Array.isArray(dataObj.errors)
      ? ((dataObj.errors as ApiErrorItem[])[0] ?? null)
      : null;
    const resultCode =
      (dataObj.resultCode as string | undefined) ??
      (dataObj.code as string | undefined) ??
      firstError?.code ??
      "NETWORK_ERROR";
    const normalized: ApiError = {
      ...dataObj,
      status: error.response?.status ?? 0,
      code: resultCode,
      resultCode,
      message: firstError?.message ?? (dataObj.message as string | undefined) ?? error.message,
      data,
    };

    return Promise.reject(normalized);
  }
);

export default httpClient;
